import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const categoryOrder = [
  { slug: 'social-content', title: 'Social Content' },
  { slug: 'beauty-wellness', title: 'Beauty & Wellness' },
  { slug: 'founder-brands', title: 'Founder Brands' },
  { slug: 'events-experiences', title: 'Events & Experiences' },
  { slug: 'editorial-campaigns', title: 'Editorial Campaigns' },
];

const galleryData = {
  'beauty-wellness': {
    title: 'Beauty & Wellness',
    images: [
      '/images/beauty-7.jpg',
      '/images/beauty-8.jpg',
      '/images/beauty-9.jpg',
      '/images/beauty-10.jpg',
      '/images/beauty-11.jpg',
      '/images/beauty-12.jpg',
      '/images/beauty-13.jpg',
    ],
  },
  'luxury-hospitality': { title: 'Luxury Hospitality', images: [] },
  'founder-brands': {
    title: 'Founder Brands',
    images: [
      '/images/founder-6.jpg',
      '/images/founder-7.jpg',
      '/images/founder-8.jpg',
      '/images/founder-9.jpg',
      '/images/founder-10.jpg',
      '/images/founder-11.jpg',
      '/images/founder-12.jpg',
      '/images/founder-13.jpg',
      '/images/founder-14.jpg',
      '/images/founder-15.jpg',
    ],
  },
  'events-experiences': {
    title: 'Events & Experiences',
    images: [
      '/images/event-51.jpg',
      '/images/event-52.jpg',
      '/images/event-53.jpg',
      '/images/event-54.jpg',
      '/images/event-55.jpg',
      '/images/event-56.jpg',
      '/images/event-57.jpg',
      '/images/event-58.jpg',
      '/images/event-59.jpg',
      '/images/event-60.jpg',
      '/images/event-61.jpg',
      '/images/event-62.png',
      '/images/founder-16.jpg',
      '/images/founder-17.jpg',
      '/images/founder-18.jpg',
      '/images/founder-19.jpg',
      '/images/event-5.jpg',
      '/images/event-6.jpg',
      '/images/event-7.jpg',
      '/images/event-8.jpg',
      '/images/event-9.jpg',
      '/images/event-10.jpg',
      '/images/event-11.jpg',
      '/images/event-12.jpg',
      '/images/event-13.jpg',
      '/images/event-14.jpg',
      '/images/event-47.jpg',
      '/images/event-48.jpg',
      '/images/event-49.jpg',
      '/images/event-50.jpg',
    ],
  },
  'editorial-campaigns': {
    title: 'Editorial Campaigns',
    images: [
      '/images/editorial-1.jpg',
      '/images/editorial-2.jpg',
      '/images/editorial-3.jpg',
      '/images/editorial-4.jpg',
      '/images/editorial-5.jpg',
      '/images/editorial-6.jpg',
      '/images/editorial-7.jpg',
      '/images/editorial-8.jpg',
      '/images/editorial-9.jpg',
      '/images/editorial-10.jpg',
      '/images/editorial-11.jpg',
      '/images/editorial-12.jpg',
      '/images/editorial-15.jpg',
      '/images/editorial-13.jpg',
      '/images/editorial-14.jpg',
      '/images/editorial-16.jpg',
    ],
  },
  'social-content': {
    title: 'Social Content',
    images: [],
    videos: [
      '/images/social-vid-6.mp4',
      '/images/social-vid-7.mp4',
      '/images/social-vid-8.mp4',
      '/images/social-vid-9.mp4',
      '/images/social-vid-10.mp4',
      '/images/social-vid-11.mp4',
      '/images/social-vid-12.mp4',
      '/images/social-vid-13.mp4',
      '/images/social-vid-14.mp4',
      '/images/social-vid-15.mp4',
      '/images/social-vid-16.mp4',
    ],
  },
};

export default function GalleryPage() {
  const { slug } = useParams();
  const gallery = galleryData[slug];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightbox, setIsLightbox] = useState(false);

  const next = useCallback(() => {
    if (!gallery?.images.length) return;
    setCurrentIndex((prev) => (prev + 1) % gallery.images.length);
  }, [gallery]);

  const prev = useCallback(() => {
    if (!gallery?.images.length) return;
    setCurrentIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
  }, [gallery]);

  useEffect(() => {
    if (!isLightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') setIsLightbox(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isLightbox, next, prev]);

  if (!gallery) {
    return (
      <div className="pt-44 min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Gallery not found.</p>
      </div>
    );
  }

  return (
    <div className="pt-44" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="gallery-hero">
        <div className="container-main">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Curated Visuals</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-8">
              {gallery.title}
            </motion.h1>
            <motion.div variants={fadeInUp}>
              <Link to="/portfolio" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                &larr; Back to Portfolio
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {(gallery.images.length > 0 || (gallery.videos && gallery.videos.length > 0)) ? (
        <>
          <div className="border-t border-border" />

          {/* Slideshow */}
          {gallery.images.length > 0 && (
          <section className="py-16 md:py-24" data-testid="gallery-slideshow">
            <div className="container-main">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-5xl mx-auto">
                {/* Main Image */}
                <motion.div variants={fadeInUp} className="relative cursor-pointer mb-8" onClick={() => setIsLightbox(true)}>
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentIndex}
                      src={gallery.images[currentIndex]}
                      alt={`${gallery.title} — ${currentIndex + 1}`}
                      className="w-full h-[60vh] md:h-[70vh] object-contain bg-white mx-auto"
                      style={{ objectPosition: 'center top' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                    />
                  </AnimatePresence>
                </motion.div>

                {/* Controls */}
                <div className="flex items-center justify-between">
                  <button onClick={prev} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="gallery-prev">
                    <ArrowLeft className="h-5 w-5" /> Previous
                  </button>
                  <span className="text-sm text-muted-foreground font-medium tracking-widest">
                    {currentIndex + 1} / {gallery.images.length}
                  </span>
                  <button onClick={next} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="gallery-next">
                    Next <ArrowRight className="h-5 w-5" />
                  </button>
                </div>

                {/* Thumbnails */}
                <div className="flex gap-3 mt-12 justify-center flex-wrap">
                  {gallery.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`w-20 h-20 overflow-hidden border-2 transition-all duration-300 ${i === currentIndex ? 'border-butter' : 'border-transparent opacity-50 hover:opacity-80'}`}
                      data-testid={`gallery-thumb-${i}`}
                    >
                      <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>
          )}

          {/* Videos */}
          {gallery.videos && gallery.videos.length > 0 && (
            <section className={`pb-16 md:pb-24 ${!gallery.images.length ? 'pt-16 md:pt-24' : ''}`} data-testid="gallery-videos">
              <div className="container-main">
                <div className="max-w-5xl mx-auto">
                  <p className="text-sm tracking-widest uppercase text-muted-foreground mb-12 text-center">In Motion</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {gallery.videos.map((vid, i) => (
                      <div key={i} className="relative w-full" style={{ paddingBottom: '177.78%' }}>
                        <video
                          className="absolute inset-0 w-full h-full object-cover bg-black"
                          controls
                          loop
                          playsInline
                          preload="metadata"
                          src={`${vid}#t=0.5`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      ) : (
        <section className="py-16 md:py-24">
          <div className="container-main text-center">
            <p className="text-muted-foreground italic">Gallery coming soon.</p>
          </div>
        </section>
      )}

      {/* Section Navigation */}
      {(() => {
        const currentIdx = categoryOrder.findIndex(c => c.slug === slug);
        const prevCat = currentIdx > 0 ? categoryOrder[currentIdx - 1] : null;
        const nextCat = currentIdx < categoryOrder.length - 1 ? categoryOrder[currentIdx + 1] : null;
        return (
          <section className="py-16 md:py-24 border-t border-border" data-testid="gallery-nav">
            <div className="container-main">
              <div className="flex items-center justify-between max-w-4xl mx-auto">
                {prevCat ? (
                  <Link to={`/portfolio/${prevCat.slug}`} className="group flex flex-col items-start gap-1" data-testid="gallery-prev-section">
                    <span className="text-xs tracking-widest uppercase text-muted-foreground">Previous</span>
                    <span className="flex items-center gap-2 font-serif text-lg md:text-xl font-medium group-hover:text-butter transition-colors duration-300">
                      <ArrowLeft className="h-4 w-4" /> {prevCat.title}
                    </span>
                  </Link>
                ) : <div />}
                {nextCat ? (
                  <Link to={`/portfolio/${nextCat.slug}`} className="group flex flex-col items-end gap-1" data-testid="gallery-next-section">
                    <span className="text-xs tracking-widest uppercase text-muted-foreground">Next</span>
                    <span className="flex items-center gap-2 font-serif text-lg md:text-xl font-medium group-hover:text-butter transition-colors duration-300">
                      {nextCat.title} <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ) : <div />}
              </div>
            </div>
          </section>
        );
      })()}

      {/* CTA */}
      <section className="py-16 md:py-24 bg-foreground" data-testid="gallery-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl font-medium leading-tight text-background mb-10">
              Interested In Creating Something Similar?
            </motion.h2>
            <motion.div variants={fadeInUp}>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="gallery-cta-btn">
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {isLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setIsLightbox(false)}
          >
            <button onClick={() => setIsLightbox(false)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50">
              <X className="h-8 w-8" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-6 text-white/60 hover:text-white z-50">
              <ArrowLeft className="h-8 w-8" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-6 text-white/60 hover:text-white z-50">
              <ArrowRight className="h-8 w-8" />
            </button>
            <img
              src={gallery.images[currentIndex]}
              alt={`${gallery.title} — ${currentIndex + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
